import React from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';

interface DismissibleAlertProps extends Omit<React.ComponentProps<typeof Alert>, 'children'> {
  onDismiss: () => void;
  /** Controls rendered next to the close button (for example a "view invoice" link). */
  actions?: React.ReactNode;
  children: React.ReactNode;
}

/** The close control shared by every alert the user dismisses by hand; named by `common.actions.close`. */
export function AlertDismissButton({ onDismiss }: { onDismiss: () => void }) {
  const { t } = useTranslation();
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className="size-7 shrink-0 pointer-coarse:size-11"
      onClick={onDismiss}
      aria-label={t('common.actions.close')}
    >
      <X className="size-4" aria-hidden="true" />
    </Button>
  );
}

/**
 * Alert the user closes by hand. The close control is a real button named by `common.actions.close`,
 * so a screen reader hears what it does instead of a bare multiplication sign.
 */
export const DismissibleAlert: React.FC<DismissibleAlertProps> = ({
  onDismiss,
  actions,
  children,
  className,
  ...alertProps
}) => {
  return (
    <Alert className={className} {...alertProps}>
      <AlertDescription className="flex items-center justify-between gap-3">
        <span className="min-w-0 wrap-break-word">{children}</span>
        <div className="flex shrink-0 items-center gap-2">
          {actions}
          <AlertDismissButton onDismiss={onDismiss} />
        </div>
      </AlertDescription>
    </Alert>
  );
};
